import React, { useState, useEffect } from "react";
import { Row, Col, FormGroup, Label, Input, Button } from "reactstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  getDriverDeductionByAll,
  createDeductionType,
  updateDriverTypeDeduction as updateSchemeDeduction
} from "../../../Services/Driver.service";
import { showAlert } from "../../../Services/Shared.service";
import { Spinner } from "../../../AbstractElements";
import SaveIcon from "@mui/icons-material/Save";

const VALUE_TYPE_OPTIONS = [
  { label: "Percentage (%)", value: "P" },
  { label: "Flat Amount (₹)", value: "A" },
];

const CALC_TYPE_OPTIONS = [
  { label: "BaseFare", value: "BaseFare" },
  { label: "TotalFare", value: "TotalFare" },
  { label: "WithAddCharge", value: "WithAddCharge" },
  { label: "None", value: "None" }
];

const BOOKING_TYPE_OPTIONS = [
  { label: "MobileApp", value: "MobileApp" },
  { label: "DriverApp", value: "DriverApp" },
  { label: "Website", value: "Website" },
  { label: "PartnerApi", value: "PartnerApi" },
  { label: "CustomerService", value: "CustomerService" },
  { label: "IRCTC", value: "IRCTC" },
  { label: "BookingWeb", value: "BookingWeb" },
  { label: "JustDial", value: "JustDial" },
  { label: "Integrator", value: "Integrator" },
  { label: "AirportBooking", value: "AirportBooking" },
  { label: "City", value: "City" },
  { label: "CRON", value: "CRON" },
  { label: "All", value: "All" },
  { label: "Others", value: "Others" },
  { label: "CallCenter", value: "CallCenter" },
];

const SingleDeductionForm = ({ deduction, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [existingTypes, setExistingTypes] = useState([]);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [formData, setFormData] = useState({
    deductionType: { label: deduction.driverDeductionName, value: deduction.driverDeductionId },
    valueType: VALUE_TYPE_OPTIONS.find(o => o.value === deduction.valueType) || VALUE_TYPE_OPTIONS[0],
    value: deduction.value || "",
    calculationType: CALC_TYPE_OPTIONS.find(o => o.value === deduction.calculateType) || CALC_TYPE_OPTIONS[0],
    bookingType: BOOKING_TYPE_OPTIONS.find(o => o.value === deduction.bookingTypeDeduction) || BOOKING_TYPE_OPTIONS.find(o => o.value === "All"),
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const resp = await getDriverDeductionByAll();
      const dataArr = resp.data?.searchResponse || resp.searchResponse || [];
      const types = dataArr.map((t) => ({
        label: t.name || t.deductionType,
        value: t.id,
      }));
      setExistingTypes(types);
    } catch (e) {
      console.error("Error fetching types", e);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.deductionType || !formData.value) {
      showAlert("error", "Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      let typeId = formData.deductionType.value;

      if (formData.deductionType.__isNew__) {
        const typeResp = await createDeductionType({
          deductionType: formData.deductionType.label,
          deductionDisplayName: formData.deductionType.label,
          calculateOn: formData.calculationType.value
        });
        typeId = typeResp.data.id;
      }

      const updateObj = {
        id: deduction.id,
        driverSchemeId: deduction.driverSchemeId,
        serviceCityId: deduction.serviceCityId,
        driverDeductionId: typeId,
        valueType: formData.valueType.value,
        value: parseFloat(formData.value),
        calculateType: formData.calculationType.value,
        bookingTypeDeduction: formData.bookingType.value,
      };

      await updateSchemeDeduction(updateObj);
      showAlert("success", "Deduction rule updated successfully!");
      onRefresh();
      onClose();
    } catch (error) {
      console.error("Error updating deduction:", error);
      const msg = error.response?.data?.message || "Failed to update deduction.";
      showAlert("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "8px",
      fontSize: "14px",
      minHeight: "45px",
      backgroundColor: state.isDisabled ? "#e9ecef" : "white",
      color: state.isDisabled ? "#6c757d" : "#333",
      borderColor: state.isFocused ? "#39B54A" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      "&:hover": {
        borderColor: state.isFocused ? "#39B54A" : (state.isDisabled ? "#dee2e6" : "#ced4da")
      }
    }),
    singleValue: (base, state) => ({ ...base, color: state.isDisabled ? "#6c757d" : "#333" }),
    placeholder: (base) => ({ ...base, color: "#999" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#39B54A"
        : state.isFocused
          ? "#E3F5E1"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: "14px",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#39B54A",
        color: "white"
      }
    })
  };

  return (
    <div className="p-3">
      <Row>
        <Col md="12">
          <FormGroup>
            <Label className="fw-bold">Deduction Type</Label>
            <CreatableSelect
              isClearable
              options={existingTypes}
              value={formData.deductionType}
              onChange={(val) => handleInputChange("deductionType", val)}
              isValidNewOption={(inputValue) => inputValue.trim().length > 0}
              placeholder="Select or Create Type..."
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Value Type</Label>
            <Select
              options={VALUE_TYPE_OPTIONS}
              value={formData.valueType}
              onChange={(val) => handleInputChange("valueType", val)}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Value</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={formData.value}
              onChange={(e) => handleInputChange("value", e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              className="form-control"
              style={{
                borderRadius: "8px",
                fontSize: "14px",
                height: "45px",
                backgroundColor: "white",
                color: "#333",
                borderColor: isInputFocused ? "#39B54A" : "#dee2e6",
                boxShadow: isInputFocused ? "0 0 0 0.2rem rgba(57, 181, 74, 0.25)" : "none",
                outline: "none"
              }}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Calculation Type</Label>
            <Select
              options={CALC_TYPE_OPTIONS}
              value={formData.calculationType}
              onChange={(val) => handleInputChange("calculationType", val)}
              styles={selectStyles}
              menuPortalTarget={document.body}
              isDisabled={formData.deductionType && !formData.deductionType.__isNew__}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label className="fw-bold">Booking Type</Label>
            <Select
              options={BOOKING_TYPE_OPTIONS}
              value={formData.bookingType}
              onChange={(val) => handleInputChange("bookingType", val)}
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex justify-content-end mt-4 pt-2">
        <Button
          color="light"
          className="me-3"
          style={{ borderRadius: "10px", fontWeight: "600", padding: "10px 25px" }}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          style={{
            borderRadius: "10px",
            fontWeight: "600",
            padding: "10px 35px",
            backgroundColor: "#39B54A",
            border: "none",
            display: "flex",
            alignItems: "center"
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <Spinner size="sm" className="me-2" />
          ) : (
            <SaveIcon fontSize="small" className="me-2" />
          )}
          Update Deduction
        </Button>
      </div>
    </div>
  );
};

export default SingleDeductionForm;
